import axios from "axios";

// Reconnaissance du parcours cinématographique,
// indépendante de la langue de l'interface.
const QUEBEC_CINEMA_CATEGORIES = [
  "Catégorie:Acteur québécois de cinéma",
  "Catégorie:Actrice québécoise de cinéma",
];

const regionCache = new Map();
const CACHE_DURATION = 60 * 60 * 1000;

export function getRegionalCareerStats(credits = []) {
  const today = new Date().toISOString().slice(0, 10);
  const films = new Map();

  for (const movie of Array.isArray(credits) ? credits : []) {
    if (!Number.isInteger(movie?.id) || movie.id <= 0) continue;

    const date = movie.release_date;

    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(date || "") ||
      date > today
    ) continue;

    const timestamp = Date.parse(`${date}T00:00:00Z`);

    if (
      !Number.isFinite(timestamp) ||
      new Date(timestamp).toISOString().slice(0, 10) !== date
    ) continue;

    // Documentaires et téléfilms exclus du comptage régional.
    if (
      movie.genre_ids?.some(
        (genre) => genre === 99 || genre === 10770
      )
    ) continue;

    const character = String(movie.character || "");

    if (
      /\b(self|himself|herself|themselves|uncredited|archive footage|archival footage)\b/i.test(
        character
      )
    ) continue;

    const top3 =
      Number.isInteger(movie.order) &&
      movie.order >= 0 &&
      movie.order <= 2;

    // Un film compte une fois ; conserver le meilleur rang connu.
    films.set(movie.id, Boolean(films.get(movie.id)) || top3);
  }

  return {
    filmCount: films.size,
    top3Count: [...films.values()].filter(Boolean).length,
  };
}

async function fetchQuebecCinemaSignal(wikidataId) {
  try {
    const { data: wikidata } = await axios.get(
      "https://www.wikidata.org/w/api.php",
      {
        timeout: 5000,
        withCredentials: false,
        params: {
          action: "wbgetentities",
          ids: wikidataId,
          props: "sitelinks",
          sitefilter: "frwiki",
          format: "json",
          origin: "*",
        },
      }
    );

    if (wikidata.error) return { status: "source-error" };

    const title =
      wikidata.entities?.[wikidataId]?.sitelinks?.frwiki?.title;

    if (!title) return { status: "missing-wikipedia" };

    const { data: wikipedia } = await axios.get(
      "https://fr.wikipedia.org/w/api.php",
      {
        timeout: 5000,
        withCredentials: false,
        params: {
          action: "query",
          titles: title,
          prop: "categories|pageprops|info",
          clcategories: QUEBEC_CINEMA_CATEGORIES.join("|"),
          cllimit: 10,
          ppprop: "wikibase_item|disambiguation",
          inprop: "url",
          redirects: 1,
          format: "json",
          formatversion: 2,
          origin: "*",
        },
      }
    );

    if (wikipedia.error) return { status: "source-error" };

    const page = wikipedia.query?.pages?.[0];

    if (
      page?.missing ||
      page?.pageprops?.wikibase_item !== wikidataId ||
      page?.pageprops?.disambiguation !== undefined
    ) return { status: "identity-mismatch" };

    const url = new URL(page.fullurl);

    if (url.origin !== "https://fr.wikipedia.org") {
      return { status: "identity-mismatch" };
    }

    const matchedCategories = (page.categories || [])
      .map((category) => category.title)
      .filter((category) =>
        QUEBEC_CINEMA_CATEGORIES.includes(category)
      );

    return {
      status: matchedCategories.length ? "matched" : "not-listed",
      matchedCategories,
      sourceUrl: url.href,
    };
  } catch {
    // Une panne ne constitue pas une preuve d'inéligibilité.
    return { status: "network-error" };
  }
}

function getCachedQuebecCinemaSignal(wikidataId) {
  const cached = regionCache.get(wikidataId);

  if (cached && cached.expiresAt > Date.now()) {
    return cached.promise;
  }

  // Cache partagé entre les langues et les requêtes simultanées.
  if (regionCache.size >= 200) {
    regionCache.delete(regionCache.keys().next().value);
  }

  const entry = {
    expiresAt: Date.now() + CACHE_DURATION,
  };

  entry.promise = fetchQuebecCinemaSignal(wikidataId).then(
    (result) => {
      // Ne pas conserver les erreurs : une prochaine visite réessaiera.
      if (
        !["matched", "not-listed", "missing-wikipedia"].includes(
          result.status
        ) &&
        regionCache.get(wikidataId) === entry
      ) {
        regionCache.delete(wikidataId);
      }

      return result;
    }
  );

  regionCache.set(wikidataId, entry);
  return entry.promise;
}

export async function getQuebecSuperStarEligibility({
  credits = [],
  wikidataId,
} = {}) {
  const stats = getRegionalCareerStats(credits);

  const result = {
    ...stats,
    qualified: false,
    scope: null,
  };

  if (stats.filmCount < 25 || stats.top3Count < 10) {
    return {
      ...result,
      status: "career-thresholds-not-met",
    };
  }

  if (!/^Q[1-9]\d*$/.test(wikidataId || "")) {
    return {
      ...result,
      status: "missing-wikidata",
    };
  }

  const signal = await getCachedQuebecCinemaSignal(wikidataId);
  const qualified = signal.status === "matched";

  return {
    ...result,
    ...signal,
    qualified,
    scope: qualified ? "quebec" : null,
  };
}